import { useState } from 'react'
import { ChevronDownIcon } from 'lucide-react'
import {
  type ConditionEvalNode,
  type TriEvalState,
  type TriggerEvalResult
} from '@axilon-apps/domains-ontology-ui-state'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { cn } from '../../lib/utils'
import { formatRelativeTime } from '../../lib/time'
import { formatTagShort } from '../../pages/ConfigurePage/util/tag-paths'
import { deriveTriggerStatus, TriggerStatusBadge } from './TriggerStatusBadge'
import { TriggerSparkline } from './TriggerSparkline'
import { resolveConditionDescription, type Condition } from '@axilon-apps/domains-ontology-ui-state'

import { describeLeafCondition, type LeafDescription } from './trigger-language'

/**
 * The minimal trigger shape this presentational list needs — just the stable
 * id (to join against the server evaluation) and optional human labels.
 * Every condition-trigger shape satisfies it, so the same rendering serves
 * custom agents and notification rules; their detail views own the
 * evaluation-query + run-stats plumbing.
 */
export interface DisplayTrigger {
  id?: string
  name?: string
  purpose?: string
  description?: string
  condition?: Condition
}

/** Per-trigger run stats (fire count + last fire), keyed by trigger id. */
export interface TriggerRunStats {
  count: number
  last?: string
}

export interface TriggerEvaluationListProps {
  triggers: DisplayTrigger[]
  /** Server-evaluated state per trigger id, from the owner's /evaluate poll. */
  evalByTriggerId: Map<string, TriggerEvalResult>
  /**
   * Optional fire-count / last-fired per trigger id. Callers may omit this
   * when run records do not identify the firing trigger.
   */
  runStatsByTriggerId?: Map<string, TriggerRunStats>
  configPaused: boolean
  /** Shown when the evaluation request failed (last-known state stays up). */
  evalError?: boolean
}

/**
 * Presentational trigger list — one status-first row per trigger, with an
 * inline sparkline for single-leaf triggers and a "show conditions" disclosure
 * for AND/OR/NOT trees. Agent-agnostic: it takes already-fetched evaluation +
 * run-stats maps as props (the wrappers do the fetching).
 */
export function TriggerEvaluationList({
  triggers,
  evalByTriggerId,
  runStatsByTriggerId,
  configPaused,
  evalError
}: TriggerEvaluationListProps) {
  if (triggers.length === 0) {
    return <p className="m-0 text-sm text-ink-3">No triggers configured.</p>
  }

  return (
    <div className="flex flex-col gap-2">
      {evalError && (
        <p className="m-0 text-xs text-ink-4">
          Evaluation request failed — showing last-known state.
        </p>
      )}
      <ul className="m-0 list-none divide-y divide-line/60 rounded-xl border border-line bg-card p-0 shadow-sm">
        {triggers.map((trigger, i) => {
          const key = trigger.id ?? String(i)
          const serverEval = trigger.id ? evalByTriggerId.get(trigger.id) : undefined
          const runStats = trigger.id ? runStatsByTriggerId?.get(trigger.id) : undefined
          return (
            <TriggerRow
              key={key}
              trigger={trigger}
              serverEval={serverEval}
              runStats={runStats}
              configPaused={configPaused}
            />
          )
        })}
      </ul>
    </div>
  )
}

interface TriggerRowProps {
  trigger: DisplayTrigger
  serverEval: TriggerEvalResult | undefined
  runStats: TriggerRunStats | undefined
  configPaused: boolean
}

function TriggerRow({ trigger, serverEval, runStats, configPaused }: TriggerRowProps) {
  const [expanded, setExpanded] = useState(false)
  const state: TriEvalState = serverEval?.state ?? 'unknown'
  const status = deriveTriggerStatus(state, configPaused)

  const description =
    trigger.name?.trim() ||
    trigger.purpose?.trim() ||
    trigger.description?.trim() ||
    serverEval?.name?.trim() ||
    serverEval?.purpose?.trim() ||
    serverEval?.description?.trim() ||
    (trigger.condition ? resolveConditionDescription(trigger.condition) : '') ||
    'Untitled trigger'

  const primaryLeaf = findPrimaryLeaf(serverEval?.nodes ?? [])
  const hasTree = (serverEval?.nodes?.length ?? 0) > 1
  const leafDesc = primaryLeaf ? describeLeafCondition(primaryLeaf) : null

  const fireCount = runStats?.count ?? 0
  const lastRun = runStats?.last

  return (
    <li className="flex flex-col">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'group/row flex w-full cursor-pointer items-start gap-4 px-4 py-3 text-left',
              'transition-colors duration-150 hover:bg-accent/30',
              'focus-visible:outline-none focus-visible:bg-accent/40'
            )}
          >
            <div className="w-28 shrink-0 pt-0.5">
              <TriggerStatusBadge status={status} />
            </div>

            <div className="min-w-0 flex-1">
              <p className="m-0 text-sm font-medium leading-snug text-ink">{description}</p>
              {(primaryLeaf?.tag_path || leafDesc?.brief) && (
                <p className="mt-1 m-0 flex flex-wrap items-center gap-x-2 text-11 text-ink-4">
                  {primaryLeaf?.tag_path && (
                    <span className="font-mono">{formatTagShort(primaryLeaf.tag_path)}</span>
                  )}
                  {leafDesc?.brief && primaryLeaf?.tag_path && <span>·</span>}
                  {leafDesc?.brief && <span>{leafDesc.brief}</span>}
                </p>
              )}
            </div>

            {primaryLeaf && (
              <div className="hidden w-35 shrink-0 items-center sm:flex">
                <TriggerSparkline
                  history={primaryLeaf.history ?? []}
                  threshold={typeof primaryLeaf.value === 'number' ? primaryLeaf.value : undefined}
                  fired={state === 'fired'}
                />
              </div>
            )}

            <div className="flex w-32 shrink-0 flex-col items-end gap-0.5 pt-0.5 text-right">
              <span className="text-xs font-medium text-ink-2">
                {fireCount === 0 ? 'Never fired' : `${fireCount} fire${fireCount === 1 ? '' : 's'}`}
              </span>
              {lastRun && <span className="text-11 text-ink-4">{formatRelativeTime(lastRun)}</span>}
            </div>

            <ChevronDownIcon
              className={cn(
                'mt-1 size-4 shrink-0 text-ink-4 transition-transform duration-200',
                expanded && 'rotate-180'
              )}
              aria-hidden
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-line/60 bg-bg-elev/30 px-4 py-3">
            <TriggerDetails
              serverEval={serverEval}
              hasTree={hasTree}
              leafDesc={leafDesc}
              isFiring={state === 'fired'}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

function TriggerDetails({
  serverEval,
  hasTree,
  leafDesc,
  isFiring
}: {
  serverEval: TriggerEvalResult | undefined
  hasTree: boolean
  leafDesc: LeafDescription | null
  isFiring: boolean
}) {
  if (!serverEval) {
    return null
  }

  return (
    <div className="flex flex-col gap-3">
      {leafDesc && <LeafEvaluationView desc={leafDesc} isFiring={isFiring} />}

      {hasTree && (
        <div className="flex flex-col gap-1.5">
          <span className="text-11 font-medium uppercase tracking-[0.06em] text-ink-4">
            Condition tree
          </span>
          <NodeTree nodes={serverEval.nodes} />
        </div>
      )}
    </div>
  )
}

export function LeafEvaluationView({
  desc,
  isFiring
}: {
  desc: LeafDescription
  isFiring: boolean
}) {
  return (
    <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs">
      <DefRow label="Watching" value={desc.watching} />
      <DefRow label="Fires when" value={desc.firesWhen} mono />
      <dt className="m-0 text-10 font-medium uppercase tracking-[0.06em] text-ink-4">Right now</dt>
      <dd className="m-0 flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-ink-2">{desc.currentNow}</span>
        <span
          className={cn(
            'text-10 uppercase tracking-[0.06em]',
            isFiring ? 'text-st-down' : 'text-st-run'
          )}
        >
          {isFiring ? '· firing' : '· not firing'}
        </span>
        {desc.currentContext && (
          <span className="font-mono text-ink-4">({desc.currentContext})</span>
        )}
      </dd>
    </dl>
  )
}

export function DefRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="m-0 text-10 font-medium uppercase tracking-[0.06em] text-ink-4">{label}</dt>
      <dd className={cn('m-0 text-ink-2', mono && 'font-mono')}>{value}</dd>
    </>
  )
}

function NodeTree({ nodes }: { nodes: ConditionEvalNode[] }) {
  if (nodes.length === 0) return null
  const [tree] = buildTree(nodes, 0)
  return <TreeNode node={tree} depth={0} />
}

interface EvalTreeNode {
  node: ConditionEvalNode
  children: EvalTreeNode[]
}

function buildTree(nodes: ConditionEvalNode[], start: number): [EvalTreeNode, number] {
  const node = nodes[start]
  const count = node.children_count ?? 0
  let idx = start + 1
  const children: EvalTreeNode[] = []
  for (let i = 0; i < count; i++) {
    if (idx >= nodes.length) break
    const [child, next] = buildTree(nodes, idx)
    children.push(child)
    idx = next
  }
  return [{ node, children }, idx]
}

function TreeNode({ node, depth }: { node: EvalTreeNode; depth: number }) {
  const { node: n, children } = node
  const isComposite = n.op === 'and' || n.op === 'or' || n.op === 'not'

  return (
    <div className={cn('flex flex-col gap-1.5', depth > 0 && 'border-l border-line/60 pl-3')}>
      <div className="flex items-center gap-2 text-xs">
        <span
          className={cn(
            'inline-flex items-center rounded-md border border-line bg-bg-elev px-1.5 py-px font-mono text-10 uppercase tracking-wider text-ink-3'
          )}
        >
          {n.op}
        </span>
        {!isComposite && n.description && <span className="text-ink-2">{n.description}</span>}
        <span
          className={cn(
            'ml-auto text-10 uppercase tracking-[0.06em]',
            n.state === 'fired'
              ? 'text-st-down'
              : n.state === 'not_fired'
                ? 'text-st-run'
                : n.state === 'error'
                  ? 'text-st-down'
                  : 'text-ink-4'
          )}
        >
          {leafStateLabel(n.state)}
        </span>
      </div>
      {children.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {children.map((child, i) => (
            <TreeNode key={i} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

function leafStateLabel(state: TriEvalState): string {
  if (state === 'fired') return 'tripped'
  if (state === 'not_fired') return 'not tripped'
  if (state === 'error') return 'error'
  return 'no data'
}

function findPrimaryLeaf(nodes: ConditionEvalNode[]): ConditionEvalNode | null {
  for (const n of nodes) {
    if (
      n.op === 'live' ||
      n.op === 'agg' ||
      n.op === 'deviation' ||
      n.op === 'alarm_active' ||
      n.op === 'expr'
    )
      return n
  }
  return null
}
